import { Planner, Todo, TodoWithDetails } from '@/common/types/databaseTypes';
import { TimeBlockContent } from '@/features/planner/components/TimeBlockContent';
import { CreatePlannerInput } from '@/features/planner/services/plannerServices';
import {
    useCreatePlanner,
    useUpdatePlanner,
} from '@/features/planner/services/usePlannerQueries';
import { useAuthStore } from '@/stores/authStore';
import { DrawerView, useTodoDrawerStore } from '@/stores/todoDrawerStore';
import { ActionIcon, Drawer, Group, Text, Tooltip } from '@mantine/core';
import { useEffect, useRef, useState } from 'react';
import { useUpdateTodo } from '../../services/useTodoQueries';
import { CategoryMenu } from '../CategoryMenu';
import { TodoActionMenu } from '../TodoActionMenu';
import { TodoDetailContent } from './TodoDetailContent';
import {
    IconChevronLeft,
    IconChevronsRight,
    IconLock,
    IconLockOpen,
    IconTrash,
} from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { getDateLocale } from '@/common/utils/i18nUtils';

interface TodoDetailDrawerProps {
    opened: boolean;
    onClose: () => void;
    // todoId: string | null; // todo id 받아 내부에서 데이터 조회
    todo: TodoWithDetails | null;
}

export const TodoDetailDrawer = ({
    opened,
    onClose,
    // todoId,
    todo,
}: TodoDetailDrawerProps) => {
    const { t } = useTranslation('todo');
    // Drawer가 닫힐 때 현재 설명 내용을 저장하기 위한 ref
    const contentRef = useRef<string>(todo?.content || '');
    const HEADER_HEIGHT = 60;
    const { user } = useAuthStore();
    const {
        initialView,
        selectedPlanner: storeSelectedPlanner,
        selectedDate: storeSelectedDate,
        targetUserId,
    } = useTodoDrawerStore();

    const [currentView, setCurrentView] = useState<DrawerView>(initialView);
    const [selectedDate, setSelectedDate] = useState<Date | null>(
        storeSelectedDate
    );
    const [selectedPlanner, setSelectedPlanner] =
        useState<Partial<Planner> | null>(storeSelectedPlanner);

    // const { data: todo, isLoading, error: todoError } = useTodo(todoId);

    const createPlanner = useCreatePlanner();
    const updatePlanner = useUpdatePlanner();
    const updateTodo = useUpdateTodo();

    // todo store와 동기화
    useEffect(() => {
        setCurrentView(initialView);
    }, [initialView]);

    useEffect(() => {
        if (storeSelectedPlanner) {
            setSelectedPlanner(storeSelectedPlanner);
        }
    }, [storeSelectedPlanner]);

    useEffect(() => {
        if (storeSelectedDate) {
            setSelectedDate(storeSelectedDate);
        }
    }, [storeSelectedDate]);

    useEffect(() => {
        // 팝오버에서 업데이트했을 때 ref도 최신 값 갖도록
        if (todo?.content !== undefined) {
            contentRef.current = todo.content;
        }
    }, [todo?.content]);

    const handleAddPlanner = async (selectedDate: Date, todoId: string) => {
        if (!user?.id) return;

        try {
            const plannerInput: CreatePlannerInput = {
                todo_id: todoId,
                blocks: [],
                selected_date: selectedDate,
                register_id: user.id,
                user_id: targetUserId ?? user.id,
            };

            const newPlanner = await createPlanner.mutateAsync(plannerInput);
            return newPlanner;
        } catch (e) {
            console.error('Failed to create planner', e);
            return null;
        }
    };

    const handleDeletePlannerRange = (
        plannerId: string,
        rangeToDelete: number[]
    ) => {
        const targetPlanner = todo?.planners?.find(
            planner => planner.id === plannerId
        );

        if (!targetPlanner) {
            console.log('플래너가 없어 업데이트 불가');
            return;
        }

        const updateBlocks = targetPlanner.blocks?.filter(
            block => !rangeToDelete.includes(block)
        );

        updatePlanner.mutate({
            id: plannerId,
            blocks: updateBlocks,
            update_id: user?.id ?? '',
        });
    };

    const handleUpdateTodo = (updates: Partial<Todo>) => {
        if (!todo) return;

        updateTodo.mutate({
            id: todo.id,
            ...updates,
            update_id: user?.id ?? '',
        });
    };

    // 현재 내용을 추적하는 콜백 함수
    const handleContentChange = (content: string) => {
        contentRef.current = content;
    };

    // 카테고리 업데이트
    const handleCategoryChange = (categoryId: string | null) => {
        handleUpdateTodo({ category_id: categoryId });
    };

    const handleTogglePublic = (isPublic: boolean) => {
        handleUpdateTodo({ is_public: !isPublic });
    };

    // 날짜 선택 - 플래너 생성
    const handleDateSelectAndCreate = async (date: Date) => {
        if (!todo) return;

        setSelectedDate(date);
        const newPlanner = await handleAddPlanner(date, todo.id);
        // if (newPlanner) {
        // setSelectedPlanner(newPlanner);
        // setCurrentView('timeBlock');
        // }
    };

    // 플래너 선택 - 시간 설정 확면으로 전환
    const handlePlannerSelect = (planner: Partial<Planner>) => {
        setSelectedPlanner(planner);
        setSelectedDate(new Date(planner.planner_date));
        setCurrentView('timeBlock');
    };

    // 시간 설정 화면 -> 투두 상세 화면으로 전환
    const handleBack = () => {
        setCurrentView('detail');
        setSelectedDate(null);
        setSelectedPlanner(null);
    };

    // Drawer가 닫히기 전에 내용 저장
    const handleCloseWithSave = () => {
        // 내용이 변경되었고 투두가 존재하는 경우에만 저장
        if (todo?.id && contentRef.current !== todo.content) {
            updateTodo.mutate(
                {
                    id: todo.id,
                    content: contentRef.current,
                    update_id: user?.id ?? '',
                },
                {
                    onSuccess: () => {
                        // 저장 완료 후 닫기
                        onClose();
                    },
                }
            );
        } else {
            // 변경사항이 없으면 바로 닫기
            onClose();
        }
    };

    const renderContent = () => {
        switch (currentView) {
            case 'timeBlock':
                return (
                    <div style={{ flex: 1 }}>
                        <TimeBlockContent
                            date={selectedDate}
                            planner={selectedPlanner}
                            isDrawer={true}
                            targetUserId={targetUserId ?? user?.id ?? ''}
                            // onBack={handleBack}
                        />
                    </div>
                );

            default:
                return todo ? (
                    <div
                        style={{
                            overflow: 'auto',
                            maxHeight: `calc(100vh - ${HEADER_HEIGHT}px)`,
                            width: '100%',
                        }}
                    >
                        <TodoDetailContent
                            todo={todo}
                            onDateSelect={handleDateSelectAndCreate}
                            onPlannerSelect={handlePlannerSelect}
                            onContentChange={handleContentChange}
                            onDeletePlannerRange={handleDeletePlannerRange}
                        />
                    </div>
                ) : null;
        }
    };

    return (
        <Drawer.Root
            opened={opened}
            onClose={
                currentView === 'detail' ? handleCloseWithSave : handleBack
            }
            radius="lg"
            position="right"
        >
            <Drawer.Overlay />
            <Drawer.Content
                style={{
                    overflow: 'visible',
                }}
            >
                <Drawer.Header style={{ height: `${HEADER_HEIGHT}px` }}>
                    <Group w="100%" justify="space-between">
                        <ActionIcon
                            onClick={
                                currentView === 'detail' ? onClose : handleBack
                            }
                            variant="subtle"
                            color="gray"
                        >
                            {currentView === 'detail' ? (
                                <IconChevronsRight />
                            ) : (
                                <IconChevronLeft />
                            )}
                        </ActionIcon>

                        {currentView === 'detail' && (
                            <Group gap="sm">
                                <CategoryMenu
                                    todo={todo}
                                    categoryId={todo?.category_id}
                                    onChangeCategory={handleCategoryChange}
                                />
                                <Tooltip
                                    label={t('component.todoDrawer.drawer.toggle_visibility', { status: todo?.is_public ? t('component.todoDrawer.drawer.private') : t('component.todoDrawer.drawer.public') })}
                                    withArrow
                                >
                                    <div>
                                        <ActionIcon
                                            variant="default"
                                            disabled={
                                                // 팀 것은 비공개 불가
                                                todo?.goal?.team_id !==
                                                undefined
                                            }
                                            onClick={() => {
                                                handleTogglePublic(
                                                    todo?.is_public ?? false
                                                );
                                            }}
                                        >
                                            {!todo.is_public ? (
                                                <IconLock
                                                    color="black"
                                                    size={16}
                                                />
                                            ) : (
                                                <IconLockOpen
                                                    color="black"
                                                    size={16}
                                                />
                                            )}
                                        </ActionIcon>
                                    </div>
                                </Tooltip>
                                <TodoActionMenu
                                    todo={todo}
                                    size="md"
                                    menuIcon={<IconTrash size="1rem" />}
                                />
                            </Group>
                        )}
                        {currentView === 'timeBlock' && (
                            <Text mx="sm">
                                {selectedDate?.toLocaleDateString(getDateLocale(), {
                                    month: 'long',
                                    day: 'numeric',
                                    weekday: 'long',
                                })}
                            </Text>
                        )}
                    </Group>
                    {/* <Drawer.CloseButton /> */}
                </Drawer.Header>
                <Drawer.Body
                    style={{
                        padding: 0,
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'auto',
                        height: '100%',
                    }}
                >
                    {renderContent()}
                </Drawer.Body>
            </Drawer.Content>
        </Drawer.Root>
    );
};
